import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { DialogContent } from "@material-ui/core";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from '@mui/material/TextField'
import {
  CircularProgress,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from '@mui/icons-material/Download'
import TerminalIcon from '@mui/icons-material/Terminal'
import { Alert } from "@mui/material";
import AlertDialog from "../../../../components/AlertDialog/index.es6";
import { PRIMARY_THEME_COLOR_SHADE_1 } from "../../../../lib/constants";
import { getPartners } from '../../../../lib/access.es6'
import { getSmsChannelsByPartnerGuid } from '../../../../lib/requests.es6'

var STEPS = [
  "Basic Config",
  "Authentication",
  "Request Details",
  "Advanced",
];

var AUTH_TYPES = [
  { value: "none", label: "None" },
  { value: "basic", label: "Basic (username / password)" },
  { value: "bearer", label: "Bearer Token" },
  { value: "api_key", label: "API Key" },
  {
    value: "oauth2_client_credentials",
    label: "OAuth2 Client Credentials",
  },
  {
    value: "oauth2_with_extra_header",
    label: "OAuth2 + Extra Header",
  },
];

var PHONE_FORMATS = [
  { value: "international", label: "International (+260971234567)" },
  { value: "international_no_plus", label: "International no plus (260971234567)" },
  { value: "local", label: "Local (0971234567)" },
  { value: "strip_country_code", label: "Strip country code (971234567)" },
  { value: 'msisdn', label: 'MSISDN (00254712695820)' },
  { value: "e164", label: "E.164 (+260971234567)" },
];

var METHODS = ["GET", "POST", "PUT"];
var CONTENT_TYPES = [
  { value: "json", label: "JSON" },
  { value: "form", label: "Form URL-encoded" },
  { value: "multipart", label: "Multipart" },
];
var KEY_PLACEMENTS = [
  { value: "header", label: "Header" },
  { value: "query", label: "Query param" },
  { value: "body", label: "Request body" },
];

function formatJsonTemplate(template) {
  if (!template) return "";
  var t = template.trim();

  // Unwrap double-encoded strings: "{\"key\": \"val\"}" → {"key": "val"}
  if (t.charAt(0) === '"' && t.charAt(t.length - 1) === '"') {
    try { t = JSON.parse(t); } catch (e) {}
  }

  // Temporarily replace {{placeholders}} so JSON.parse doesn't choke
  var slots = [];
  var sanitized = t.replace(/\{\{[^}]+\}\}/g, function(match) {
    var token = "__PH" + slots.length + "__";
    slots.push({ token: token, original: match });
    return '"' + token + '"';
  });

  try {
    var pretty = JSON.stringify(JSON.parse(sanitized), null, 2);
    slots.forEach(function(s) {
      pretty = pretty.split('"' + s.token + '"').join(s.original);
    });
    return pretty;
  } catch (e) {
    return t;
  }
}

function parseTemplateJson(jsonStr) {
  if (!jsonStr) return null;
  var slots = [];
  // Handle "{{...}}" (whole quoted value) first to avoid double-quoting
  var sanitized = jsonStr.replace(/"(\{\{[\w.-]+\}\})"/g, function(_, match) {
    var token = '__PH' + slots.length + '__';
    slots.push({ token: token, original: match });
    return '"' + token + '"';
  });
  // Handle remaining {{...}} embedded inside strings or unquoted
  sanitized = sanitized.replace(/\{\{[\w.-]+\}\}/g, function(match) {
    var token = '__PH' + slots.length + '__';
    slots.push({ token: token, original: match });
    return '"' + token + '"';
  });
  try {
    var parsed = JSON.parse(sanitized);
    function restore(val) {
      if (typeof val === "string") {
        return slots.reduce(function(s, entry) {
          return s.split('"' + entry.token + '"').join(entry.original)
                  .split(entry.token).join(entry.original);
        }, val);
      }
      if (Array.isArray(val)) return val.map(restore);
      if (val && typeof val === "object") {
        var out = {};
        Object.keys(val).forEach(function(k) { out[k] = restore(val[k]); });
        return out;
      }
      return val;
    }
    return restore(parsed);
  } catch (e) {
    return null;
  }
}

function tokenizeCurl(str) {
  const s = str.replace(/\\\s*[\r\n]+\s*/g, ' ').trim()
  const tokens = []
  let i = 0
  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i])) i += 1
    if (i >= s.length) break
    const c = s[i]
    if (c === "'" || c === '"') {
      const quote = c
      i += 1
      let buf = ''
      while (i < s.length && s[i] !== quote) {
        if (s[i] === '\\' && i + 1 < s.length) { buf += s[i + 1]; i += 2 }
        else { buf += s[i]; i += 1 }
      }
      tokens.push(buf)
      i += 1
    } else {
      const start = i
      while (i < s.length && !/\s/.test(s[i])) i += 1
      tokens.push(s.slice(start, i))
    }
  }
  return tokens
}

const CURL_IGNORE_FLAGS = {
  '--location': true, '-L': true, '-s': true, '--silent': true,
  '-k': true, '--insecure': true, '-v': true, '--verbose': true,
  '--compressed': true, '-g': true, '--globoff': true,
}

function safeDecodeQueryValue(str) {
  // Try standard decode first; if it fails (e.g. Kannel specifiers like %d, %t, %F, %A
  // are not valid 2-hex-digit sequences), fall back to decoding only valid %XX patterns.
  try {
    return decodeURIComponent(str);
  } catch (e) {
    return str.replace(/%([0-9A-Fa-f]{2})/g, function(match) {
      try { return decodeURIComponent(match); } catch (e2) { return match; }
    });
  }
}

function parseCurl(curlStr) {
  const tokens = tokenizeCurl(curlStr)
  let url = null
  let method = null
  const headers = {}
  let body = null
  let basicAuth = null

  let i = tokens[0] && tokens[0].toLowerCase() === 'curl' ? 1 : 0

  while (i < tokens.length) {
    const tok = tokens[i]

    if (tok === '-X' || tok === '--request') {
      i += 1
      if (i < tokens.length) method = tokens[i]
    } else if (tok === '-H' || tok === '--header') {
      i += 1
      if (i < tokens.length) {
        const hdr = tokens[i]
        const ci = hdr.indexOf(':')
        if (ci > 0) headers[hdr.slice(0, ci).trim()] = hdr.slice(ci + 1).trim()
      }
    } else if (
      tok === '-d' || tok === '--data' ||
      tok === '--data-raw' || tok === '--data-binary' ||
      tok === '--data-urlencode'
    ) {
      i += 1
      if (i < tokens.length) body = tokens[i]
    } else if (tok === '-u' || tok === '--user') {
      i += 1
      if (i < tokens.length) basicAuth = tokens[i]
    } else if (tok === '--url') {
      i += 1
      if (i < tokens.length) url = tokens[i]
    } else if (CURL_IGNORE_FLAGS[tok]) {
      // skip
    } else if (!tok.startsWith('-') && !url) {
      url = tok
    } else if (tok.startsWith('-') && !tok.startsWith('--') && tok.length === 2) {
      i += 1 // skip value of unknown short flag
    }
    i += 1
  }

  const patch = {}

  if (url) {
    const qIdx = url.indexOf('?')
    if (qIdx >= 0) {
      patch.endpoint = url.slice(0, qIdx)
      const queryObj = {}
      url.slice(qIdx + 1).split('&').forEach(function(pair) {
        const eIdx = pair.indexOf('=')
        if (eIdx >= 0) {
          queryObj[safeDecodeQueryValue(pair.slice(0, eIdx))] = safeDecodeQueryValue(pair.slice(eIdx + 1))
        } else if (pair) {
          queryObj[pair] = ''
        }
      })
      if (Object.keys(queryObj).length > 0) {
        patch.query_params_json = JSON.stringify(queryObj, null, 2)
      }
    } else {
      patch.endpoint = url
    }
  }

  if (method) {
    const mu = method.toUpperCase()
    if (mu === 'GET' || mu === 'POST' || mu === 'PUT') patch.method = mu
  } else if (body) {
    patch.method = 'POST'
  }

  const ct = headers['Content-Type'] || headers['content-type']
  if (ct) {
    if (ct.indexOf('application/json') >= 0) patch.content_type = 'json'
    else if (ct.indexOf('application/x-www-form-urlencoded') >= 0) patch.content_type = 'form'
    else if (ct.indexOf('multipart') >= 0) patch.content_type = 'multipart'
    delete headers['Content-Type']
    delete headers['content-type']
  }

  const authHdr = headers['Authorization'] || headers['authorization']
  if (authHdr) {
    if (authHdr.indexOf('Bearer ') === 0) {
      patch.auth_type = 'bearer'
      patch.auth_token = authHdr.slice(7)
    } else if (authHdr.indexOf('Basic ') === 0) {
      patch.auth_type = 'basic'
      try {
        const decoded = atob(authHdr.slice(6))
        const ci2 = decoded.indexOf(':')
        if (ci2 >= 0) {
          patch.auth_username = decoded.slice(0, ci2)
          patch.auth_password = decoded.slice(ci2 + 1)
        }
      } catch (e) { /* invalid base64 */ }
    } else {
      patch.auth_type = 'api_key'
      patch.auth_key_name = 'Authorization'
      patch.auth_key_value = authHdr
      patch.auth_key_placement = 'header'
    }
    delete headers['Authorization']
    delete headers['authorization']
  } else if (basicAuth) {
    patch.auth_type = 'basic'
    const ci3 = basicAuth.indexOf(':')
    if (ci3 >= 0) {
      patch.auth_username = basicAuth.slice(0, ci3)
      patch.auth_password = basicAuth.slice(ci3 + 1)
    }
  }

  if (Object.keys(headers).length > 0) {
    patch.headers_json = JSON.stringify(headers, null, 2)
  }

  if (body) {
    patch.body_template = formatJsonTemplate(body)
  }

  return patch
}

function buildFormState(channel) {
  if (!channel) {
    return {
      channel_name: "",
      method: "POST",
      endpoint: "",
      phone_format: "international",
      auth_type: "none",
      auth_username: "",
      auth_password: "",
      auth_token: "",
      auth_key_name: "",
      auth_key_value: "",
      auth_key_placement: "header",
      auth_token_url: "",
      auth_client_id: "",
      auth_client_secret: "",
      auth_scope: "",
      auth_extra_header_name: "",
      auth_extra_header_value_from_response: "",
      content_type: "json",
      body_template: "",
      headers_json: "",
      query_params_json: "",
      variables_json: "",
      message_id_field: "",
      success_enabled: false,
      success_http_codes: "",
      success_response_field: "",
      success_response_value: "",
      success_failure_field: "",
      success_failure_value: "",
      callback_enabled: false,
      callback_status_field: "",
      callback_status_mapping: JSON.stringify(
        {
          DELIVERED: "DELIVERED",
          FAILED: "FAILED",
        },
        null,
        2
      ),
    };
  }

  var auth = channel.auth || { type: "none" };
  var sc = channel.success_condition || null;
  var cb = channel.callback || null;

  return {
    channel_name: channel.channel_name || "",
    method: channel.method || "POST",
    endpoint: channel.endpoint || "",
    phone_format: channel.phone_format || (channel.variables_map && channel.variables_map.phone_format) || "international",
    auth_type: auth.type || "none",
    auth_username: auth.username || "",
    auth_password: auth.password || "",
    auth_token: auth.token || "",
    auth_key_name: auth.key_name || "",
    auth_key_value: auth.key_value || "",
    auth_key_placement: auth.placement || "header",
    auth_token_url: auth.token_url || "",
    auth_client_id: auth.client_id || "",
    auth_client_secret: auth.client_secret || "",
    auth_scope: auth.scope || "",
    auth_extra_header_name: auth.extra_header_name || "",
    auth_extra_header_value_from_response:
      auth.extra_header_value_from_response || "",
    content_type: channel.content_type || "json",
    body_template: formatJsonTemplate(channel.body_template || ""),
    headers_json: channel.headers
      ? JSON.stringify(channel.headers, null, 2)
      : "",
    query_params_json: channel.query_params
      ? JSON.stringify(channel.query_params, null, 2)
      : "",
    variables_json: (channel.variables_map && channel.variables_map.extras)
      ? JSON.stringify(channel.variables_map.extras, null, 2)
      : "",
    message_id_field: channel.message_id_field || "",
    success_enabled: !!sc,
    success_http_codes:
      sc && sc.http_status_codes ? sc.http_status_codes.join(", ") : "",
    success_response_field: (sc && sc.response_field) || "",
    success_response_value: (sc && sc.response_value) || "",
    success_failure_field: (sc && sc.failure_field) || "",
    success_failure_value: (sc && sc.failure_value) || "",
    callback_enabled: !!(cb && cb.enabled),
    callback_status_field: (cb && cb.status_field) || "",
    callback_status_mapping:
      cb && cb.status_mapping
        ? JSON.stringify(cb.status_mapping, null, 2)
        : JSON.stringify({ DELIVERED: "DELIVERED", FAILED: "FAILED" }, null, 2),
  };
}

function buildPayload(s) {
  var channel = {
    method: s.method,
    endpoint: s.endpoint,
  };

  if (s.channel_name) channel.channel_name = s.channel_name;
  if (s.phone_format && s.phone_format !== "international") {
    channel.phone_format = s.phone_format;
  }
  if (s.content_type && s.content_type !== "json") {
    channel.content_type = s.content_type;
  }
  if (s.body_template) {
    channel.body_template = formatJsonTemplate(s.body_template);
  }
  if (s.message_id_field) channel.message_id_field = s.message_id_field;

  var authType = s.auth_type;
  if (authType && authType !== "none") {
    if (authType === "basic") {
      channel.auth = {
        type: "basic",
        username: s.auth_username,
        password: s.auth_password,
      };
    } else if (authType === "bearer") {
      channel.auth = { type: "bearer", token: s.auth_token };
    } else if (authType === "api_key") {
      channel.auth = {
        type: "api_key",
        key_name: s.auth_key_name,
        key_value: s.auth_key_value,
        placement: s.auth_key_placement,
      };
    } else if (
      authType === "oauth2_client_credentials" ||
      authType === "oauth2_with_extra_header"
    ) {
      channel.auth = {
        type: authType,
        token_url: s.auth_token_url,
        client_id: s.auth_client_id,
        client_secret: s.auth_client_secret,
      };
      if (s.auth_scope) channel.auth.scope = s.auth_scope;
      if (authType === "oauth2_with_extra_header") {
        channel.auth.extra_header_name = s.auth_extra_header_name;
        channel.auth.extra_header_value_from_response =
          s.auth_extra_header_value_from_response;
      }
    }
  }

  if (s.headers_json) {
    var parsedHeaders = parseTemplateJson(s.headers_json);
    if (parsedHeaders) channel.headers = parsedHeaders;
  }
  if (s.query_params_json) {
    var parsedParams = parseTemplateJson(s.query_params_json);
    if (parsedParams) channel.query_params = parsedParams;
  }
  var vm = {};
  if (s.variables_json) {
    var parsedExtras = parseTemplateJson(s.variables_json);
    if (parsedExtras) vm.extras = parsedExtras;
  }
  if (s.phone_format && s.phone_format !== "international") {
    vm.phone_format = s.phone_format;
  }
  if (Object.keys(vm).length) channel.variables_map = vm;

  if (s.success_enabled) {
    channel.success_condition = {};
    if (s.success_http_codes) {
      channel.success_condition.http_status_codes = s.success_http_codes
        .split(",")
        .map(function(x) { return parseInt(x.trim()); })
        .filter(function(n) { return !isNaN(n); });
    }
    if (s.success_response_field) {
      channel.success_condition.response_field = s.success_response_field;
    }
    if (s.success_response_value) {
      channel.success_condition.response_value = s.success_response_value;
    }
    if (s.success_failure_field) {
      channel.success_condition.failure_field = s.success_failure_field;
    }
    if (s.success_failure_value) {
      channel.success_condition.failure_value = s.success_failure_value;
    }
  }

  if (s.callback_enabled) {
    channel.callback = {
      enabled: true,
      type: "push",
      status_field: s.callback_status_field,
    };
    if (s.callback_status_mapping) {
      try {
        channel.callback.status_mapping = JSON.parse(s.callback_status_mapping);
      } catch (e) {}
    }
  }

  return channel;
}

export default function AddEditSmsChannel(props) {
  var isEdit = !!props.existingChannel;
  var open = props.open;

  var formRef = React.useRef(null);
  var [activeStep, setActiveStep] = React.useState(0);
  var [form, setForm] = React.useState(
    buildFormState(props.existingChannel)
  );
  var [showSecrets, setShowSecrets] = React.useState({});
  var [testPhone, setTestPhone] = React.useState("");
  var [testMessage, setTestMessage] = React.useState(
    "Test SMS from Aspin — please ignore."
  );
  var [testDryRun, setTestDryRun] = React.useState(false);

  const [curlOpen, setCurlOpen] = React.useState(false)
  const [curlText, setCurlText] = React.useState('')

  const [importOpen, setImportOpen] = React.useState(false)
  const [importPartnerGuid, setImportPartnerGuid] = React.useState('')
  const [importChannels, setImportChannels] = React.useState([])
  const [importChannelName, setImportChannelName] = React.useState(null)
  const [importLoading, setImportLoading] = React.useState(false)
  const [importError, setImportError] = React.useState(null)

  React.useEffect(
    function() {
      if (open) {
        setActiveStep(0);
        setForm(buildFormState(props.existingChannel));
        setShowSecrets({});
        setTestPhone("");
        setTestMessage("Test SMS from Aspin — please ignore.");
        setTestDryRun(false);
        setCurlOpen(false)
        setCurlText('')
        setImportOpen(false)
        setImportPartnerGuid('')
        setImportChannels([])
        setImportChannelName(null)
        setImportError(null)
        if (props.onTestReset) props.onTestReset();
      }
    },
    [open]
  );

  React.useEffect(function() {
    ValidatorForm.addValidationRule("isValidJson", function(value) {
      if (!value) return true;
      try {
        var sanitized = value
          .replace(/"(\{\{[\w.-]+\}\})"/g, '"__placeholder__"')
          .replace(/\{\{[\w.-]+\}\}/g, '"__placeholder__"');
        JSON.parse(sanitized);
        return true;
      } catch (e) { return false; }
    });
    return function() { ValidatorForm.removeValidationRule("isValidJson"); };
  }, []);

  function set(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  function toggleSecret(field) {
    setShowSecrets(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = !prev[field];
      return next;
    });
  }

  function secretAdornment(field) {
    return {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            size="small"
            onClick={function() { toggleSecret(field); }}
          >
            {showSecrets[field] ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
        </InputAdornment>
      ),
    };
  }

  function handleNext() {
    if (!formRef.current) {
      setActiveStep(function(s) { return s + 1; });
      return;
    }
    formRef.current.isFormValid(false).then(function(isValid) {
      if (isValid) setActiveStep(function(s) { return s + 1; });
    });
  }
  function handleBack() {
    setActiveStep(function(s) { return s - 1; });
  }

  function handleSubmit() {
    var payload = buildPayload(form);
    props.onSave(payload);
  }

  function handleSaveSuccess() {
    props.onSaveSuccess();
    setActiveStep(STEPS.length - 1);
  }

  function handleSaveError() {
    props.onSaveErrorReset();
  }

  function handleTest() {
    if (!props.onTest) return;
    var payload = { phone: testPhone, message: testMessage, dry_run: testDryRun };
    var channelName = form.channel_name || (props.existingChannel && props.existingChannel.channel_name);
    if (channelName) payload.channel_name = channelName;
    props.onTest(payload);
  }

  function handleCurlApply() {
    const patch = parseCurl(curlText)
    setForm(function(prev) { return Object.assign({}, prev, patch) })
    setCurlOpen(false)
    setCurlText('')
    setActiveStep(0)
  }

  function handleImportPartnerChange(guid) {
    setImportPartnerGuid(guid)
    setImportChannels([])
    setImportChannelName(null)
    setImportError(null)
    if (!guid) return
    setImportLoading(true)
    getSmsChannelsByPartnerGuid(guid)
      .then(function(response) {
        const channels =
          (response.data &&
            response.data.data &&
            response.data.data.sms_channels) ||
          []
        setImportChannels(channels)
        setImportLoading(false)
      })
      .catch(function() {
        setImportError('Failed to load channels for this partner')
        setImportLoading(false)
      })
  }

  function handleImportConfirm() {
    const selected = importChannels.find(function(c) {
      return (c.channel_name || '') === (importChannelName || '')
    })
    if (!selected) return
    setForm(buildFormState(selected))
    setImportOpen(false)
    setImportPartnerGuid('')
    setImportChannels([])
    setImportChannelName(null)
    setImportError(null)
  }

  function renderTestResult() {
    var result = props.testResult;
    var error = props.testError;
    if (!result && !error) return null;

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          {typeof error === "string" ? error : JSON.stringify(error)}
        </Alert>
      );
    }

    return (
      <Box mt={1}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          {result.success
            ? <CheckCircleIcon color="success" fontSize="small" />
            : <CancelIcon color="error" fontSize="small" />}
          <Typography variant="body2" fontWeight="bold">
            {result.success ? "Test sent successfully" : "Test failed"}
          </Typography>
          {result.message_id && (
            <Typography variant="caption" color="text.secondary">
              MsgID: {result.message_id}
            </Typography>
          )}
        </Box>
        {result.error && (
          <Alert severity="warning" sx={{ mb: 1 }}>{result.error}</Alert>
        )}
        {result.provider_request && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">Request:</Typography>
            <Box
              component="pre"
              sx={{
                background: "#f5f5f5",
                borderRadius: 1,
                p: 1,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                overflowX: "auto",
                maxHeight: 140,
                mt: 0.5,
              }}
            >
              {JSON.stringify(
                (function(req) {
                  if (!req || typeof req.body !== "string") return req
                  try { return Object.assign({}, req, { body: JSON.parse(req.body) }) }
                  catch (e) { return req }
                })(result.provider_request),
                null,
                2
              )}
            </Box>
          </Box>
        )}
        {result.provider_response && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">Response:</Typography>
            <Box
              component="pre"
              sx={{
                background: "#f5f5f5",
                borderRadius: 1,
                p: 1,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                overflowX: "auto",
                maxHeight: 140,
                mt: 0.5,
              }}
            >
              {JSON.stringify(result.provider_response, null, 2)}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  var isLastStep = activeStep === STEPS.length - 1;

  return (
    <>
      <AlertDialog
        custom
        show={props.saveLoading}
        size="sm"
        style={{ marginTop: "0", top: "30vh" }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Saving SMS channel...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.saveSuccess}
        size="sm"
        title={"SMS channel saved successfully"}
        style={{ marginTop: "0", top: "30vh" }}
        onConfirm={handleSaveSuccess}
        confirmBtnText={"OK"}
        showCancel={false}
        confirmBtnCssClass
      />

      <AlertDialog
        danger
        show={!!props.saveError}
        title={"Error saving SMS channel"}
        onConfirm={handleSaveError}
        confirmBtnText={"Try again"}
        showCancel={false}
        style={{ marginTop: "0", top: "30vh" }}
        confirmBtnCssClass
      >
        {props.saveError
          ? typeof props.saveError === "string"
            ? props.saveError
            : props.saveError.message || "Unknown error"
          : ""}
      </AlertDialog>

      {/* ── Import cURL Dialog ────────────────────────────── */}
      <Dialog
        open={curlOpen}
        onClose={function() { setCurlOpen(false) }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import from cURL</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Paste a cURL command. Endpoint, method, headers, auth, body and query
              params will be extracted automatically. Replace any hardcoded phone or
              message values with <code>{'{{phone}}'}</code> and{' '}
              <code>{'{{message}}'}</code> after importing.
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={10}
              label="cURL command"
              value={curlText}
              onChange={function(e) { setCurlText(e.target.value) }}
              variant="outlined"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
              placeholder={'curl -X POST \'https://api.example.com/sms\' \\\n  -H \'Authorization: Bearer TOKEN\' \\\n  -H \'Content-Type: application/json\' \\\n  -d \'{"to":"{{phone}}","text":"{{message}}"}\''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={function() { setCurlOpen(false) }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!curlText.trim()}
            onClick={handleCurlApply}
          >
            Parse &amp; Fill Form
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import Config Dialog ──────────────────────────── */}
      <Dialog
        open={importOpen}
        onClose={function() { setImportOpen(false) }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import configuration from another partner</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              fullWidth
              label="Partner"
              value={importPartnerGuid}
              onChange={function(e) { handleImportPartnerChange(e.target.value) }}
              variant="outlined"
              size="small"
            >
              {getPartners().map(function(p) {
                return (
                  <MenuItem key={p.guid} value={p.guid}>
                    {p.name}
                  </MenuItem>
                )
              })}
            </TextField>

            {importLoading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading channels…</Typography>
              </Box>
            )}

            {!importLoading && importPartnerGuid && importChannels.length === 0 && !importError && (
              <Alert severity="info">No SMS channels configured for this partner.</Alert>
            )}

            {importError && (
              <Alert severity="error">{importError}</Alert>
            )}

            {importChannels.length > 0 && (
              <TextField
                select
                fullWidth
                label="Channel"
                value={importChannelName === null ? '' : importChannelName}
                onChange={function(e) { setImportChannelName(e.target.value) }}
                variant="outlined"
                size="small"
              >
                {importChannels.map(function(c) {
                  return (
                    <MenuItem key={c.channel_name || '_default'} value={c.channel_name || ''}>
                      {c.channel_name || '(default)'}
                    </MenuItem>
                  )
                })}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={function() { setImportOpen(false) }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={importChannelName === null}
            onClick={handleImportConfirm}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        fullScreen
        onClose={props.onClose}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={props.onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              {isEdit
                ? "Edit SMS Channel: " +
                  (props.existingChannel.channel_name || "(default)")
                : "Add SMS Channel"}
            </Typography>
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              startIcon={<TerminalIcon fontSize="small" />}
              onClick={function() { setCurlOpen(true) }}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', mr: 1 }}
            >
              Import cURL
            </Button>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <Box sx={{ width: "100%", mt: 2 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {STEPS.map(function(label) {
                return (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>

            <ValidatorForm ref={formRef} onSubmit={handleSubmit}>
              {/* ── Step 0: Basic Config ─────────────────────────── */}
              {activeStep === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Basic Channel Settings
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item lg={4} md={4} xs={12}>
                    <TextValidator
                      fullWidth
                      label="Channel Name"
                      name="channel_name"
                      value={form.channel_name}
                      onChange={function(e) { set("channel_name", e.target.value); }}
                      variant="outlined"
                      validators={['required']}
                      errorMessages={['Channel name is required']}
                      helperText='Unique identifier for this channel, e.g. "mtn", "airtel", "default".'
                    />
                  </Grid>

                  <Grid item lg={4} md={4} xs={12}>
                    <TextValidator
                      select
                      fullWidth
                      label="HTTP Method"
                      name="method"
                      value={form.method}
                      onChange={function(e) { set("method", e.target.value); }}
                      variant="outlined"
                      validators={["required"]}
                      errorMessages={["Method is required"]}
                    >
                      {METHODS.map(function(m) {
                        return (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        );
                      })}
                    </TextValidator>
                  </Grid>

                  <Grid item lg={4} md={4} xs={12}>
                    <TextValidator
                      select
                      fullWidth
                      label="Phone Number Format"
                      name="phone_format"
                      value={form.phone_format}
                      onChange={function(e) { set("phone_format", e.target.value); }}
                      variant="outlined"
                    >
                      {PHONE_FORMATS.map(function(f) {
                        return (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        );
                      })}
                    </TextValidator>
                  </Grid>

                  <Grid item xs={12}>
                    <TextValidator
                      fullWidth
                      label="Endpoint URL"
                      name="endpoint"
                      value={form.endpoint}
                      onChange={function(e) { set("endpoint", e.target.value); }}
                      variant="outlined"
                      validators={["required"]}
                      errorMessages={["Endpoint URL is required"]}
                      helperText="Use {{phone}}, {{message}}, {{sender}}, {{request_id}}, {{callback_url}}, {{env.KEY}} as template variables."
                    />
                  </Grid>

                  <Grid item lg={4} md={4} xs={12}>
                    <TextValidator
                      fullWidth
                      label="Message ID Field (JSONPath)"
                      name="message_id_field"
                      value={form.message_id_field}
                      onChange={function(e) { set("message_id_field", e.target.value); }}
                      variant="outlined"
                      helperText='JSONPath to extract message ID from response. e.g. $.data.messageId'
                    />
                  </Grid>
                </Grid>
              )}

              {/* ── Step 1: Authentication ───────────────────────── */}
              {activeStep === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Authentication
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item lg={4} md={4} xs={12}>
                    <TextValidator
                      select
                      fullWidth
                      label="Auth Type"
                      value={form.auth_type}
                      onChange={function(e) { set("auth_type", e.target.value); }}
                      variant="outlined"
                    >
                      {AUTH_TYPES.map(function(a) {
                        return (
                          <MenuItem key={a.value} value={a.value}>
                            {a.label}
                          </MenuItem>
                        );
                      })}
                    </TextValidator>
                  </Grid>

                  {form.auth_type === "basic" && (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Username"
                          value={form.auth_username}
                          onChange={function(e) { set("auth_username", e.target.value); }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["Username is required"]}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Password"
                          value={form.auth_password}
                          onChange={function(e) { set("auth_password", e.target.value); }}
                          variant="outlined"
                          type={showSecrets["auth_password"] ? "text" : "password"}
                          InputProps={secretAdornment("auth_password")}
                          validators={["required"]}
                          errorMessages={["Password is required"]}
                        />
                      </Grid>
                    </>
                  )}

                  {form.auth_type === "bearer" && (
                    <Grid item lg={8} md={8} xs={12}>
                      <TextValidator
                        fullWidth
                        label="Bearer Token"
                        value={form.auth_token}
                        onChange={function(e) { set("auth_token", e.target.value); }}
                        variant="outlined"
                        type={showSecrets["auth_token"] ? "text" : "password"}
                        InputProps={secretAdornment("auth_token")}
                        validators={["required"]}
                        errorMessages={["Token is required"]}
                      />
                    </Grid>
                  )}

                  {form.auth_type === "api_key" && (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Key Name"
                          value={form.auth_key_name}
                          onChange={function(e) { set("auth_key_name", e.target.value); }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["Key name is required"]}
                          helperText='e.g. "X-API-Key" or "apikey"'
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Key Value"
                          value={form.auth_key_value}
                          onChange={function(e) { set("auth_key_value", e.target.value); }}
                          variant="outlined"
                          type={showSecrets["auth_key_value"] ? "text" : "password"}
                          InputProps={secretAdornment("auth_key_value")}
                          validators={["required"]}
                          errorMessages={["Key value is required"]}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          select
                          fullWidth
                          label="Key Placement"
                          value={form.auth_key_placement}
                          onChange={function(e) { set("auth_key_placement", e.target.value); }}
                          variant="outlined"
                        >
                          {KEY_PLACEMENTS.map(function(p) {
                            return (
                              <MenuItem key={p.value} value={p.value}>
                                {p.label}
                              </MenuItem>
                            );
                          })}
                        </TextValidator>
                      </Grid>
                    </>
                  )}

                  {(form.auth_type === "oauth2_client_credentials" ||
                    form.auth_type === "oauth2_with_extra_header") && (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Token URL"
                          value={form.auth_token_url}
                          onChange={function(e) { set("auth_token_url", e.target.value); }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["Token URL is required"]}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Client ID"
                          value={form.auth_client_id}
                          onChange={function(e) { set("auth_client_id", e.target.value); }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["Client ID is required"]}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Client Secret"
                          value={form.auth_client_secret}
                          onChange={function(e) { set("auth_client_secret", e.target.value); }}
                          variant="outlined"
                          type={showSecrets["auth_client_secret"] ? "text" : "password"}
                          InputProps={secretAdornment("auth_client_secret")}
                          validators={["required"]}
                          errorMessages={["Client Secret is required"]}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Scope (optional)"
                          value={form.auth_scope}
                          onChange={function(e) { set("auth_scope", e.target.value); }}
                          variant="outlined"
                        />
                      </Grid>
                    </>
                  )}

                  {form.auth_type === "oauth2_with_extra_header" && (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Extra Header Name"
                          value={form.auth_extra_header_name}
                          onChange={function(e) { set("auth_extra_header_name", e.target.value); }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["Header name is required"]}
                          helperText='e.g. "X-API-Key"'
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Header value — JSONPath in token response"
                          value={form.auth_extra_header_value_from_response}
                          onChange={function(e) {
                            set(
                              "auth_extra_header_value_from_response",
                              e.target.value
                            );
                          }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["JSONPath is required"]}
                          helperText='e.g. $.api_key — extracted from the OAuth2 token response'
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              )}

              {/* ── Step 2: Request Details ──────────────────────── */}
              {activeStep === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Request Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item lg={4} md={4} xs={12}>
                    <TextValidator
                      select
                      fullWidth
                      label="Content Type"
                      value={form.content_type}
                      onChange={function(e) { set("content_type", e.target.value); }}
                      variant="outlined"
                    >
                      {CONTENT_TYPES.map(function(ct) {
                        return (
                          <MenuItem key={ct.value} value={ct.value}>
                            {ct.label}
                          </MenuItem>
                        );
                      })}
                    </TextValidator>
                  </Grid>

                  <Grid item xs={12}>
                    <TextValidator
                      fullWidth
                      multiline
                      rows={8}
                      label="Body Template (JSON)"
                      value={form.body_template}
                      onChange={function(e) { set("body_template", e.target.value); }}
                      variant="outlined"
                      helperText="Type clean JSON. Use {{phone}}, {{message}}, {{sender}}, {{request_id}}, {{callback_url}}, {{extras.key}}, {{env.KEY}} as template variables."
                      inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <TextValidator
                      fullWidth
                      multiline
                      rows={5}
                      label='Custom Headers (JSON)'
                      value={form.headers_json}
                      onChange={function(e) { set("headers_json", e.target.value); }}
                      variant="outlined"
                      validators={["isValidJson"]}
                      errorMessages={["Must be valid JSON"]}
                      helperText='e.g. {"X-Sender-Id": "ASPIN"}. Supports {{env.KEY}} and {{extras.key}}.'
                      inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <TextValidator
                      fullWidth
                      multiline
                      rows={5}
                      label='Query Params (JSON)'
                      value={form.query_params_json}
                      onChange={function(e) { set("query_params_json", e.target.value); }}
                      variant="outlined"
                      validators={["isValidJson"]}
                      errorMessages={["Must be valid JSON"]}
                      helperText='e.g. {"sender": "{{sender}}", "apikey": "{{env.API_KEY}}"}'
                      inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextValidator
                      fullWidth
                      multiline
                      rows={4}
                      label='Extra Variables (JSON)'
                      value={form.variables_json}
                      onChange={function(e) { set("variables_json", e.target.value); }}
                      variant="outlined"
                      validators={["isValidJson"]}
                      errorMessages={["Must be valid JSON"]}
                      helperText='Custom key→value pairs available as {{extras.key}} in all templates.'
                      inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                    />
                  </Grid>
                </Grid>
              )}

              {/* ── Step 3: Advanced ────────────────────────────── */}
              {activeStep === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Success Condition
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.success_enabled}
                          onChange={function(e) {
                            set("success_enabled", e.target.checked);
                          }}
                          color="primary"
                        />
                      }
                      label="Configure custom success condition (default: any 2xx is success)"
                    />
                  </Grid>

                  {form.success_enabled && (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Accepted HTTP status codes"
                          value={form.success_http_codes}
                          onChange={function(e) { set("success_http_codes", e.target.value); }}
                          variant="outlined"
                          helperText="Comma-separated, e.g. 200, 201"
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Success field (JSONPath)"
                          value={form.success_response_field}
                          onChange={function(e) { set("success_response_field", e.target.value); }}
                          variant="outlined"
                          helperText="e.g. $.status"
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Success value"
                          value={form.success_response_value}
                          onChange={function(e) { set("success_response_value", e.target.value); }}
                          variant="outlined"
                          helperText="e.g. OK"
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Failure field (JSONPath)"
                          value={form.success_failure_field}
                          onChange={function(e) { set("success_failure_field", e.target.value); }}
                          variant="outlined"
                          helperText="e.g. $.error"
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Failure value"
                          value={form.success_failure_value}
                          onChange={function(e) { set("success_failure_value", e.target.value); }}
                          variant="outlined"
                          helperText="e.g. FAILED"
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Delivery Callback (Push)
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.callback_enabled}
                          onChange={function(e) {
                            set("callback_enabled", e.target.checked);
                          }}
                          color="primary"
                        />
                      }
                      label="Enable delivery callback (provider calls back with delivery status)"
                    />
                  </Grid>

                  {form.callback_enabled && (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Status field (JSONPath)"
                          value={form.callback_status_field}
                          onChange={function(e) { set("callback_status_field", e.target.value); }}
                          variant="outlined"
                          validators={["required"]}
                          errorMessages={["Status field is required"]}
                          helperText="e.g. $.status or $.report"
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextValidator
                          fullWidth
                          multiline
                          rows={6}
                          label="Status Mapping (JSON)"
                          value={form.callback_status_mapping}
                          onChange={function(e) {
                            set("callback_status_mapping", e.target.value);
                          }}
                          variant="outlined"
                          helperText='Map provider values to: DELIVERED, UNDELIVERED, FAILED, SENT, or IGNORE. e.g. {"1": "DELIVERED", "2": "FAILED"}'
                          inputProps={{
                            style: {
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  {/* ── Test Channel ─────────────────────────────── */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Test Channel
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  {!isEdit ? (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Save the channel first, then re-open it to send a test SMS.
                      </Alert>
                    </Grid>
                  ) : (
                    <>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          label="Recipient Phone"
                          value={testPhone}
                          onChange={function(e) { setTestPhone(e.target.value); }}
                          variant="outlined"
                          validators={activeStep === 3 && testPhone !== "" ? ["required"] : []}
                          errorMessages={["Phone is required"]}
                          helperText="e.g. +260971234567"
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <TextValidator
                          fullWidth
                          multiline
                          rows={2}
                          label="Message"
                          value={testMessage}
                          onChange={function(e) { setTestMessage(e.target.value); }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item lg={4} md={4} xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={testDryRun}
                              onChange={function(e) { setTestDryRun(e.target.checked); }}
                              color="primary"
                            />
                          }
                          label="Dry run (no HTTP call)"
                        />
                        <Box mt={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              props.testLoading
                                ? <CircularProgress size={14} />
                                : <SendIcon fontSize="small" />
                            }
                            disabled={props.testLoading || !testPhone}
                            onClick={handleTest}
                          >
                            {testDryRun ? "Dry Run" : "Send Test"}
                          </Button>
                        </Box>
                      </Grid>

                      {(props.testResult || props.testError) && (
                        <Grid item xs={12}>
                          {renderTestResult()}
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              )}

              {/* ── Navigation buttons ──────────────────────────── */}
              <Box
                display="flex"
                justifyContent="space-between"
                mt={4}
                pt={2}
                borderTop="1px solid #e0e0e0"
              >
                <Box display="flex" gap={1}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    variant="outlined"
                  >
                    Back
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={function() { setImportOpen(true) }}
                  >
                    Import Config
                  </Button>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={function() {
                      setForm(buildFormState(props.existingChannel));
                      setActiveStep(0);
                    }}
                  >
                    Reset
                  </Button>
                  {!isLastStep ? (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                    >
                      {isEdit ? "Save Changes" : "Add Channel"}
                    </Button>
                  )}
                </Box>
              </Box>
            </ValidatorForm>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
